import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { consumePostAuthRedirect } from '../lib/postAuthRedirect';
import { landingStateForPath } from '../lib/signInLanding';
import { Book, Loader2, AlertCircle } from 'lucide-react';

export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Email links (signup verification, magic links) land here, often in a
    // fresh tab where router state is gone. If a page promised "we'll bring
    // you back" before the inbox round-trip (e.g. /redeem), it persisted the
    // destination — honour it, otherwise the default flow from `/`.
    //
    // Single-shot: the auth event AND the getSession() check below can BOTH
    // fire for one landing, and the second would consume an already-empty
    // store and clobber the promised destination with '/'. First one wins.
    let navigated = false;
    const go = () => {
      if (navigated) return;
      navigated = true;
      const destination = consumePostAuthRedirect() ?? '/';
      // A restored Home is a sign-in arrival, so a pass-holder still gets
      // forwarded to their school's pack (lib/signInLanding).
      navigate(destination, { replace: true, state: landingStateForPath(destination) });
    };

    // Supabase automatically handles the token in the URL and fires onAuthStateChange.
    // We just need to wait for the session to be established.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
        go();
      }
    });

    // Also check if there's already a session (e.g. page reload)
    supabase.auth.getSession().then(({ data: { session }, error: sessionError }) => {
      if (sessionError) {
        setError(sessionError.message);
        return;
      }
      if (session) {
        go();
      }
    });

    // Timeout fallback — if nothing happens in 10s, show an error
    const timeout = setTimeout(() => {
      setError('The magic link may have expired. Please request a new one.');
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="bg-white p-10 rounded-2xl shadow-sm border border-gray-100 max-w-sm w-full text-center">
        <div className="flex items-center justify-center gap-2 text-primary-700 font-bold text-xl mb-6">
          <Book className="h-6 w-6" />
          <span>ThinkShow</span>
        </div>

        {error ? (
          <>
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Link expired</h2>
            <p className="text-sm text-gray-500 mb-6">{error}</p>
            <a
              href="/login"
              className="inline-block text-sm text-primary-600 font-medium hover:underline"
            >
              Back to Sign In
            </a>
          </>
        ) : (
          <>
            <Loader2 className="h-12 w-12 text-primary-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Signing you in...</h2>
            <p className="text-sm text-gray-500">Please wait while we verify your magic link.</p>
          </>
        )}
      </div>
    </div>
  );
};
